import { getMonth } from "date-fns";
import moment from "moment/moment";

export enum ShowroomLocation {
  Vlaardingen = "Vlaardingen",
  Amsterdam = "Amsterdam",
}
/**
 * It returns a string of the current date, or the first day of the current month, or the first day of the current year
 * @param {number | undefined} firstMonth - The first month of the year.
 * @param {number | undefined} firstYear - The first year of the date range.
 * @returns A string in the format of "dd-mm-yyyy"
 */
export const getCurrentDate = (firstMonth?: number, firstYear?: number) => {
  const today = new Date();
  const date = {
    dd: firstMonth ? 1 : today.getDate(),
    mm: firstMonth ?? today.getMonth() + 1,
    yyyy: firstYear ?? today.getFullYear(),
  };
  return getDateString(date.dd, date.mm, date.yyyy);
};

/**
 * It returns a string representing the date of the last day of the month, given a month and year
 * @param {number | undefined} firstMonth - The number of months to add to the current month.
 * @param {number | undefined} finalYear - number | undefined
 * @returns A string in the format of dd/mm/yyyy
 */
export const getFinalDate = (
  firstMonth?: number,
  finalYear?: number,
): string => {
  const today = new Date();
  const month = firstMonth ?? 0;
  const year = finalYear ?? today.getFullYear();
  const days = new Date(year, month, 0).getDate();
  return getDateString(days, month, year);
};

export const getDateString = (
  day: number,
  month: number,
  year: number,
): string => {
  const yyyy = String(year);
  const mm = String(month).padStart(2, "0"); //January is 0!
  const dd = String(day).padStart(2, "0"); //January is 0!
  return `${yyyy}-${mm}-${dd}`;
};

export const getFormattedBumbalRequest = (date: Date) => {
  let formattedDate = moment(date);
  let from = formattedDate.format("YYYY-MM-DD") + " 07:30";
  let to = formattedDate.format("YYYY-MM-DD") + " 18:00";

  return {
    dateFrom: from,
    dateTo: to,
  };
};

/**
 * It takes a date object and returns a string with the date in Dutch
 * @param {Date} date - Date - The date you want to format
 * @returns A string with the date in the format: "day of the week, day of the month, month, year"
 */
export const getLocalDateString = (date: Date) => {
  return date.toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export const filterHolidayDates = (date: any, location: string) => {
  const day = date.getDate(); // get the day in 1-31 format
  const month = getMonth(date) + 1; // Month is from 0, so + 1 (1-12)

  const selectedHolidayDates =
    location === ShowroomLocation.Vlaardingen
      ? vlaardingenHolidayDates
      : amsterdamHolidayDates;
  // If the given date matches any of the holiday dates, then it should be disallowed
  const isHoliday = selectedHolidayDates.some((holiday: any) => {
    return holiday.day === day && holiday.month === month;
  });

  // Return whether it is a holiday date or not
  return !isHoliday;
};

export const months = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];

// Holiday dates on which pickup/delivery is not available
const holidayDates = [
  // Ma. 1 jan. - Nieuwjaarsdag - gesloten
  { day: 1, month: 1 },

  // Eerste en tweede paasdag (Easter Sunday and Easter Monday):
  { day: 20, month: 4 },

  // Za. 26 apr. - Koningsdag - gesloten
  { day: 26, month: 4 },
  // Bevrijdingsdag (Liberation Day): Monday 5 May 2025
  { day: 5, month: 5 },
  //Hemelvaartsdag (Ascension Day): Thursday 29 May 2025
  { day: 29, month: 5 },
  //Eerste en tweede pinksterdag (Whit Sunday and Whit Monday):
  { day: 8, month: 6 },

  // Do. 5 dec. - Sinterklaas - houd rekening met eerder dicht(geen koopavond) afhalen tot 17.00 uur
  { day: 5, month: 12 },
  // Wo. 25 dec. - 1e kerstdag - gesloten
  { day: 25, month: 12 },
  // Zo. 31 dec. - Oudjaarsdag - 11.00 - 16.00
  { day: 31, month: 12 },
];

const vlaardingenHolidayDates = [...holidayDates, { day: 26, month: 12 }];
const amsterdamHolidayDates = [...holidayDates];
